/**
 * Cursors between people whose pictures differ. Every page lays the graph out by itself, and in
 * 3D picks its own depths, so a bare world point would sit on different nodes for two people
 * (and drift when one of them turns the camera). So a cursor is attached to a node:
 *
 * - **sending** ({@link pointerCursor}): the node under the pointer (in 3D: the first one its line
 *   of sight hits), else the nearest one on screen, and the offset from it to the pointer; in 3D
 *   also the line of sight itself (`ray`);
 * - **showing** ({@link cursorPoint}): by this page's own copy of that node, plus the offset.
 *   The bare `x, y, z` is only used for a node this page does not have.
 *
 * Pure functions of a {@link Scene}: the presence service sends what they build, the graph
 * window's people layer draws what they resolve.
 */
import type { Cursor } from "@protocol/Cursor";
import type { Scene, SimNode } from "./scene";
import type { Vec3 } from "./camera";

/** How long the drawn part of a 3D cursor's ray is, in world units (it ends at the cursor). */
export const RAY_LENGTH = 240;

/**
 * The cursor for a pointer at `(px, py)` on the graph's canvas, or `null` when it cannot be
 * worked out (a number of it is not one: the view or the node it is attached to is broken).
 */
export function pointerCursor(scene: Scene, px: number, py: number): Cursor | null {
  const in_3d = scene.dimension === "3d";
  const hit = scene.hitNode(px, py);
  const node = hit ?? nearestOnScreen(scene, px, py);
  // On the node's own depth, so in 3D the cursor sits where the line of sight meets it.
  const at = node ? scene.planePoint(px, py, node) : scene.toWorld(px, py);
  const ray = scene.rayAt(px, py);
  const cursor: Cursor = {
    x: at.x,
    y: at.y,
    z: at.z,
    in_3d,
    anchor: node ? { node: node.id, dx: at.x - node.x, dy: at.y - node.y, dz: in_3d ? at.z - node.z : 0, on: !!hit } : null,
    ray: ray ? [ray.x, ray.y, ray.z] : null,
  };
  const numbers = [cursor.x, cursor.y, cursor.z, ...(cursor.anchor ? [cursor.anchor.dx, cursor.anchor.dy, cursor.anchor.dz] : []), ...(cursor.ray ?? [])];
  return numbers.every(Number.isFinite) ? cursor : null;
}

/** Where `cursor` is in this page's world: by this page's copy of its node when it has it. */
export function cursorPoint(scene: Scene, cursor: Cursor): Vec3 {
  const node = anchorNode(scene, cursor);
  if (!node || !cursor.anchor) return { x: cursor.x, y: cursor.y, z: cursor.z };
  return { x: node.x + cursor.anchor.dx, y: node.y + cursor.anchor.dy, z: node.z + cursor.anchor.dz };
}

/** The node the cursor's pointer (or line of sight) is on, in this page, when shown. */
export function pointedNode(scene: Scene, cursor: Cursor): SimNode | null {
  return cursor.anchor?.on ? anchorNode(scene, cursor) : null;
}

/** Where the drawn part of the cursor's ray starts (it ends at `tip`), or `null` without a ray. */
export function rayStart(cursor: Cursor, tip: Vec3): Vec3 | null {
  if (!cursor.ray) return null;
  const [x, y, z] = cursor.ray;
  return { x: tip.x - x * RAY_LENGTH, y: tip.y - y * RAY_LENGTH, z: tip.z - z * RAY_LENGTH };
}

/** This page's copy of the cursor's node, if it is shown. */
function anchorNode(scene: Scene, cursor: Cursor): SimNode | null {
  const node = cursor.anchor ? scene.byId.get(cursor.anchor.node) : undefined;
  return node?.visible ? node : null;
}

/** The visible node drawn closest to a screen point. */
function nearestOnScreen(scene: Scene, px: number, py: number): SimNode | null {
  let best: SimNode | null = null;
  let bestDistance = Infinity;
  for (const n of scene.nodes) {
    if (!n.visible) continue;
    const d = Math.hypot(scene.sx(n) - px, scene.sy(n) - py);
    if (d < bestDistance) {
      bestDistance = d;
      best = n;
    }
  }
  return best;
}
